// utils/calculations.js

/**
 * Calculates SL distance or SL price based on user input mode:
 */
export function calcStopLoss({
    stopLossMode,
    stopLossValue,
    tradeDirection,
    askPrice,
    bidPrice,
    pipSize,
    atrValue,
    entryPrice: customEntryPrice // New parameter for custom entry price
  }) {


    const entryPrice = customEntryPrice ?? (tradeDirection === "BUY" ? askPrice : bidPrice);

    if (!pipSize || !tradeDirection || !entryPrice) return { slDistance: null, slPrice: null };
    const parsedStopLossValue = parseFloat(stopLossValue);

    if (stopLossMode !== "atr" && (stopLossValue === "" || isNaN(parsedStopLossValue))) {
        return { slDistance: null, slPrice: null };
    }

    switch (stopLossMode) {
        case "distance":
            const distancePrice =
                tradeDirection === "BUY"
                    ? entryPrice - parsedStopLossValue * pipSize
                    : entryPrice + parsedStopLossValue * pipSize;
            return { slDistance: parsedStopLossValue, slPrice: parseFloat(distancePrice.toFixed(5)) };

        case "price":
            const distance =
                tradeDirection === "BUY"
                    ? (entryPrice - parsedStopLossValue) / pipSize
                    : (parsedStopLossValue - entryPrice) / pipSize;
            return { slDistance: parseFloat(distance.toFixed(1)), slPrice: parsedStopLossValue };

        case "atr":
            if (stopLossValue === null || isNaN(parsedStopLossValue) || !pipSize) {
                return { slDistance: null, slPrice: null };
            }
            // stopLossValue is the ATR price offset (e.g., 0.0015)
            const atrPriceOffset = parsedStopLossValue;
            
            // 1. Calculate the SL Price directly
            const atrSlPrice =
                tradeDirection === "BUY"
                    ? entryPrice - atrPriceOffset
                    : entryPrice + atrPriceOffset;

            // 2. Calculate the SL Distance in pips from the price
            const atrSlDistance =
                tradeDirection === "BUY"
                    ? (entryPrice - atrSlPrice) / pipSize
                    : (atrSlPrice - entryPrice) / pipSize;

            return { 
                slDistance: parseFloat(atrSlDistance.toFixed(1)), 
                slPrice: parseFloat(atrSlPrice.toFixed(5)) 
            };

        default:
            return { slDistance: null, slPrice: null };
    }
}

export function calcTakeProfit({
    takeProfitMode,
    takeProfitValue,
    stopLossDistance,
    tradeDirection,
    askPrice,
    bidPrice,
    pipSize,
    entryPrice: customEntryPrice // New parameter for custom entry price
}) {
    const entryPrice = customEntryPrice ?? (tradeDirection === "BUY" ? askPrice : bidPrice);

    if (!pipSize || !tradeDirection || !entryPrice) return { tpDistance: null, tpPrice: null };
    let parsedTakeProfitValue = parseFloat(takeProfitValue);

    if (takeProfitMode === "price") {
        parsedTakeProfitValue = parseFloat(takeProfitValue); // takeProfitValue is actually takeProfitPrice in this mode
        if (takeProfitValue === "" || isNaN(parsedTakeProfitValue)) {
            return { tpDistance: null, tpPrice: null };
        }
    } else if (takeProfitValue === "" || isNaN(parsedTakeProfitValue)) {
        return { tpDistance: null, tpPrice: null };
    }

    switch (takeProfitMode) {
        case "distance":
            const distancePrice =
                tradeDirection === "BUY"
                    ? entryPrice + parsedTakeProfitValue * pipSize
                    : entryPrice - parsedTakeProfitValue * pipSize;
            return { tpDistance: parsedTakeProfitValue, tpPrice: parseFloat(distancePrice.toFixed(5)) };

        case "price":
            const distance =
                tradeDirection === "BUY"
                    ? (parsedTakeProfitValue - entryPrice) / pipSize
                    : (entryPrice - parsedTakeProfitValue) / pipSize;
            return { tpDistance: parseFloat(distance.toFixed(1)), tpPrice: parsedTakeProfitValue };

        case "rr_ratio":  // Uses R:R to calculate Take Profit
            if (stopLossDistance === null || isNaN(stopLossDistance)) {
                return { tpDistance: null, tpPrice: null };
            }
            const rrDistance = stopLossDistance * parsedTakeProfitValue;
            const rrPrice =
                tradeDirection === "BUY"
                    ? entryPrice + rrDistance * pipSize
                    : entryPrice - rrDistance * pipSize;
            return { tpDistance: rrDistance, tpPrice: parseFloat(rrPrice.toFixed(5)) };

        default:
            return { tpDistance: null, tpPrice: null };
    }
}

function resolveCapEntryPrice({
    entryPrice,
    accountCurrency,
    symbolCurrency,
    priceConversionRate,
}) {
    const entry = Number(entryPrice);
    if (!Number.isFinite(entry) || entry <= 0) {
        return entry;
    }

    const accountCcy = String(accountCurrency || '').toUpperCase();
    const symbolCcy = String(symbolCurrency || '').toUpperCase();
    if (!accountCcy || !symbolCcy || accountCcy === symbolCcy) {
        return entry;
    }

    const rate = Number(priceConversionRate);
    if (Number.isFinite(rate) && rate > 0) {
        return entry * rate;
    }

    return entry;
}
  
  export function calcTradeValues({ 
    accountEquity, 
    riskPercent, 
    slDistance, 
    tpDistance, 
    pipSize, 
    contractSize,
    freeMargin,
    cashAvailable,
    accountCurrency,
    symbolCurrency,
    priceConversionRate,
    entryPrice,
}) {
    /*console.log("calcTradeValues Inputs:", 
      { 
        accountEquity, 
        riskPercent, 
        slDistance, 
        tpDistance, 
        pipSize, 
        contractSize 
    });*/

    if (!accountEquity || !riskPercent || !slDistance || !pipSize || !contractSize) {
        //console.log("calcTradeValues Missing Data – Returning N/A");
        return { lotSize: "N/A", projectedLoss: "N/A", projectedProfit: "N/A" };
    }

    // Risk Amount = Account Equity × (Risk % / 100)
    const riskAmount = (accountEquity * (riskPercent / 100));
    const pipValue = contractSize * pipSize;
    let lotSize = riskAmount / (slDistance * pipValue);

    // For stocks (contractSize <= 1), cap lot to available buying power
    if (contractSize <= 1 && entryPrice > 0) {
        const capValue = (cashAvailable && cashAvailable > 0)
            ? cashAvailable
            : ((freeMargin && freeMargin > 0) ? freeMargin : accountEquity);
        if (capValue > 0) {
            const capEntryPrice = resolveCapEntryPrice({
                entryPrice,
                accountCurrency,
                symbolCurrency,
                priceConversionRate,
            });
            // Keep in sync with backend buffer in risk/management.py
            // (IB's pre-trade check uses an estimated order value that can
            // exceed our notional by 4-5% on cross-currency stock trades.)
            const cashBuffer = 0.92;
            const maxLot = (capValue * cashBuffer) / capEntryPrice;
            if (lotSize > maxLot) lotSize = maxLot;
        }
    }
    
    // Projected Loss = Lot Size × Stop Loss Distance × Pip Value
    const projectedLoss = lotSize * slDistance * pipValue;

    // Projected Profit = Lot Size × Take Profit Distance × Pip Value
    const projectedProfit = lotSize * tpDistance * pipValue;

    //console.log("calcTradeValues Output:", { lotSize: lotSize.toFixed(2), projectedLoss, projectedProfit });

    return {
        lotSize: lotSize.toFixed(2),
        projectedLoss: projectedLoss.toFixed(2),
        projectedProfit: projectedProfit.toFixed(2)
    };
}

/**
 * Multi-target P/L calculator.
 */
export function calcMultiValues({
  accountEquity,
  riskPercent,
  slDistance,
  targets,       // [{ share, tp_type, rr, atr_multiplier, price }]
  pipSize,
  contractSize,
  askPrice,
  bidPrice,
  tradeDirection,
  atrValue,      // pass in from your ATR fetch
  entryPrice: customEntryPrice,
  freeMargin,
    cashAvailable,
    accountCurrency,
    symbolCurrency,
    priceConversionRate,
}) {
  // 1) risk amount & pip value
  const riskAmount = accountEquity * (riskPercent / 100);
  const pipValue   = pipSize * contractSize;

  // 2) lot size as before
  let lotSize    = riskAmount / (slDistance * pipValue);

  // 3) for each target leg compute its profit and price
  let totalProfit = 0;
  let totalRR     = 0;
  const entryPrice = customEntryPrice ?? (tradeDirection === 'BUY' ? askPrice : bidPrice);

  // Cap for stocks
  if (contractSize <= 1 && entryPrice > 0) {
        const capValue = (cashAvailable && cashAvailable > 0)
            ? cashAvailable
            : ((freeMargin && freeMargin > 0) ? freeMargin : accountEquity);
    if (capValue > 0) {
            const capEntryPrice = resolveCapEntryPrice({
                entryPrice,
                accountCurrency,
                symbolCurrency,
                priceConversionRate,
            });
            // Keep in sync with backend buffer in risk/management.py
            const cashBuffer = 0.92;
            const maxLot = (capValue * cashBuffer) / capEntryPrice;
      if (lotSize > maxLot) lotSize = maxLot;
    }
  }

  const targetsWithCalculatedPrices = targets.map(t => {
    let tpDistance = 0;
    let calculatedTargetPrice = 0;

    switch (t.tp_type) {
      case 'RR':
        tpDistance = t.rr * slDistance;
        totalRR   += t.share * t.rr;
        calculatedTargetPrice = tradeDirection === 'BUY'
            ? entryPrice + tpDistance * pipSize
            : entryPrice - tpDistance * pipSize;
        break;
      case 'ATR':
        if (atrValue && slDistance > 0) { // Ensure atrValue is available and slDistance is positive
            tpDistance = t.atr_multiplier * atrValue;
            totalRR   += t.share * (tpDistance / slDistance);
            calculatedTargetPrice = tradeDirection === 'BUY'
                ? entryPrice + tpDistance * pipSize
                : entryPrice - tpDistance * pipSize;
        } else {
            // Handle case where ATR is not available or SL distance is zero
            calculatedTargetPrice = entryPrice; // Or some other default/error indicator
        }
        break;
      case 'PRICE':
        // Ensure t.price is a valid number before parsing
        const targetPriceInput = parseFloat(t.price);
        if (!isNaN(targetPriceInput)) {
            tpDistance = Math.abs(targetPriceInput - entryPrice) / pipSize;
            totalRR   += t.share * (tpDistance / slDistance);
            calculatedTargetPrice = targetPriceInput;
        } else {
            // Handle invalid price input
            calculatedTargetPrice = entryPrice; // Or some other default/error indicator
            tpDistance = 0; // Avoid NaN issues in profit calculation
        }
        break;
      default:
        calculatedTargetPrice = entryPrice; // Default for unknown types
    }

    // allocate share of lot to this leg → profit leg
    totalProfit += lotSize * t.share * tpDistance * pipValue;

    return {
      ...t,
      calculatedPrice: parseFloat(calculatedTargetPrice.toFixed(5))
    };
  });

  return {
    lotSize:         lotSize.toFixed(2),
    projectedLoss:   (lotSize * slDistance * pipValue).toFixed(2),
    projectedProfit: totalProfit.toFixed(2),
    weightedRR:      totalRR,       // if you want to display an average RR
    updatedTargets:  targetsWithCalculatedPrices,
  };
}
